/* ==========================================================================
   impact-bar.js — sticky cumulative impact meter (Greenspark tiers)

   Load ONCE, globally, in layout/theme.liquid just before </body>,
   AFTER free-gift.js so the two agree on cart state:

     <script src="{{ 'free-gift.js' | asset_url }}" defer></script>
     <script src="{{ 'impact-bar.js' | asset_url }}" defer></script>

   Render the markup with {% render 'impact-bar' %} directly after your
   header snippet in theme.liquid.

   IMPORTANT — this bar is DISPLAY ONLY. It mirrors what Greenspark will
   fund; it never funds anything itself. Greenspark's own tiered-spend
   trigger runs server-side off the real order. If you change the $30
   threshold here, change it there too, or the bar will start lying.
   ========================================================================== */

(function () {
  "use strict";

  if (window.__ecoImpactBar) return;          // never initialise twice
  window.__ecoImpactBar = true;

  /* ---- Config. Cents, CAD.
     These are FALLBACKS only — the real values come from the section
     settings via data- attributes, so the theme editor is the single
     source of truth. Greenspark's trigger still has to be changed by
     hand to match. ---- */
  var TIER_CENTS = 3000;                       // one bundle per $30
  var SHIP_CENTS = 4500;                       // free shipping
  var SHIP_LABEL = "Free shipping";

  function readSettings(root) {
    var t = parseInt(root.getAttribute("data-tier"), 10);
    var sh = parseInt(root.getAttribute("data-ship"), 10);
    if (t > 0) TIER_CENTS = t;
    if (sh > 0) SHIP_CENTS = sh;
    SHIP_LABEL = root.getAttribute("data-ship-label") || SHIP_LABEL;
  }

  /* One bundle, exactly as configured in Greenspark ("Tiered-Ecoesse").
     `per` is the quantity delivered by ONE bundle. */
  var BUNDLE = [
    { label: "Trees planted",   sub: "Brazil",       per: 1  },
    { label: "Kelp planted",    sub: "Canada",       per: 1  },
    { label: "Bees protected",  sub: "Kenya",        per: 20 },
    { label: "Plastic items recovered", sub: "",     per: 5  },
    { label: "Days of clean water", sub: "Sierra Leone", per: 1 },
    { label: "Carbon offset",   sub: "",             per: 10, unit: "kg" }
  ];

  /* Headline pills — keep to three, they have to fit one phone line */
  var PILLS = [
    { icon: "\uD83C\uDF33", per: 1,  word: "trees" },
    { icon: "\uD83C\uDF0A", per: 1,  word: "kelp"  },
    { icon: "\uD83D\uDC1D", per: 20, word: "bees"  }
  ];

  /* Kept even though free-gift.js is gone: if any app or future reward
     ever stamps a _reward line, it must not count toward a tier. */
  var GIFT_PROPERTY = "_reward";

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  var CART_ROUTE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var el = {};
  var lastTiers = 0;
  var booted = false;
  var timer = null;

  /* ---------------------------------------------------------------- */

  function money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      try { return window.Shopify.formatMoney(cents, "${{amount_no_decimals}}"); } catch (e) {}
    }
    return "$" + Math.round(cents / 100);
  }

  function isGift(item) {
    return !!(item.properties && item.properties[GIFT_PROPERTY]);
  }

  /* Total that actually counts toward a tier */
  function qualifyingTotal(cart) {
    var total = cart.total_price;
    cart.items.forEach(function (item) {
      if (isGift(item)) total -= item.final_line_price;
    });
    return Math.max(total, 0);
  }

  function cache() {
    var root = document.getElementById("eco-impact-bar");
    if (!root) return false;
    el.root  = root;
    el.mult  = root.querySelector("[data-eco-mult]");
    el.next  = root.querySelector("[data-eco-next]");
    el.fill  = root.querySelector("[data-eco-fill]");
    el.pills = root.querySelector("[data-eco-pills]");
    el.marks = root.querySelector("[data-eco-marks]");
    el.rw    = root.querySelector("[data-eco-rewards]");
    el.ldg   = root.querySelector("[data-eco-ledger]");
    el.live  = root.querySelector("[data-eco-live]");
    readSettings(root);
    return true;
  }

  /* ---------------------------------------------------------------- */

  function paint(total) {
    var tiers  = Math.floor(total / TIER_CENTS);
    var into   = total % TIER_CENTS;
    var toTier = TIER_CENTS - into;
    var shipOn = total >= SHIP_CENTS;

    el.root.classList.toggle("eco--empty", total === 0);

    /* ---- Phase 1: below free shipping, the track runs absolutely from $0
       to the shipping threshold, so the truck marker sits at a fixed,
       meaningful place and impact tiers show as ticks along the way.
       Phase 2: shipping is earned, so the track becomes the repeating
       impact tier — the only mechanic still running. ---- */
    var pct, marks = "";

    if (!shipOn) {
      pct = (total / SHIP_CENTS) * 100;
      for (var c = TIER_CENTS; c < SHIP_CENTS; c += TIER_CENTS) {
        marks += '<i class="eco-tick" style="left:' + (c / SHIP_CENTS * 100) + '%"></i>';
      }
      marks += '<i class="eco-flag" style="left:100%" title="' + SHIP_LABEL + '">\uD83D\uDE9A</i>';
    } else {
      pct = into === 0 ? 100 : (into / TIER_CENTS) * 100;
    }

    /* Set on the ROOT as a custom property, matching how rewards-bar.liquid
       does it — the width lands even if the fill node is re-rendered. */
    el.root.style.setProperty("--eco-fill", pct + "%");
    if (el.fill) el.fill.style.width = pct + "%";
    el.marks.innerHTML = marks;

    el.mult.innerHTML = '<span>IMPACT</span>' + (tiers > 0 ? "\u00D7" + tiers : "\u2014");

    /* ---- Next milestone: whichever unearned thing is closest ---- */
    if (total === 0) {
      el.next.innerHTML = el.root.getAttribute("data-empty") ||
        "Add anything to start<br>your impact";
    } else {
      var candidates = [{ at: total + toTier, msg: "impact \u00D7" + (tiers + 1) }];
      if (!shipOn) candidates.push({ at: SHIP_CENTS, msg: SHIP_LABEL.toLowerCase() });
      candidates.sort(function (a, b) { return a.at - b.at; });

      /* Milestones landing on the same dollar merge into one line —
         one target pulls harder than two racing each other. */
      var nearest = candidates[0];
      var merged  = candidates.filter(function (c) { return c.at === nearest.at; })
                              .map(function (c) { return c.msg; });

      el.next.innerHTML = "<b>" + money(nearest.at - total) + "</b> more \u2192<br>" +
        merged.join(" + ");
    }

    el.pills.innerHTML = PILLS.map(function (p) {
      return '<span class="eco-pill">' + p.icon + " <em>" + (p.per * tiers) + "</em> " + p.word + "</span>";
    }).join("") + '<span class="eco-hint">' +
      (el.root.classList.contains("eco--open") ? "CLOSE" : "DETAILS") + "</span>";

    el.rw.innerHTML = '<span class="eco-rw' + (shipOn ? " eco-rw--on" : "") + '">\uD83D\uDE9A ' +
      (shipOn ? SHIP_LABEL + " unlocked" : SHIP_LABEL + " at " + money(SHIP_CENTS)) + "</span>";

    el.ldg.innerHTML = BUNDLE.map(function (b) {
      return '<div class="eco-lrow' + (tiers ? "" : " eco-lrow--dim") + '">' +
        "<dt>" + b.label + (b.sub ? ' <i>' + b.sub + "</i>" : "") + "</dt>" +
        "<dd>" + (b.per * tiers) + (b.unit || "") + "</dd></div>";
    }).join("") +
      '<div class="eco-lrow eco-lrow--sum"><dt>Bundles funded</dt><dd>' +
      tiers + " \u00D7 " + money(TIER_CENTS) + "</dd></div>";

    if (booted && tiers > lastTiers) {
      el.root.classList.remove("eco--pop");
      void el.root.offsetWidth;
      el.root.classList.add("eco--pop");
      el.live.textContent = "Impact bundle " + tiers + " unlocked.";
    }
    lastTiers = tiers;
    booted = true;
  }

  function sync() {
    if (!el.root && !cache()) return;
    fetch(ROOT + "cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { paint(qualifyingTotal(cart)); })
      .catch(function (e) { console.warn("[impact-bar]", e.message); });
  }

  /* Debounced, and deliberately slower than free-gift.js's 200ms sweep so
     we read the cart AFTER it has finished adding or removing a gift line. */
  function queue() {
    clearTimeout(timer);
    timer = setTimeout(sync, 320);
  }

  /* ---- Expand / collapse the ledger ---- */
  function wire() {
    if (!cache()) return;
    var head = el.root.querySelector("[data-eco-toggle]");
    function toggle() {
      var open = el.root.classList.toggle("eco--open");
      head.setAttribute("aria-expanded", open ? "true" : "false");
    }
    head.addEventListener("click", toggle);
    head.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
    });
  }

  /* ---- Catch cart changes however the theme makes them ---- */
  var nativeFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    return nativeFetch.apply(this, args).then(function (res) {
      try {
        var input = args[0];
        var url = typeof input === "string" ? input : (input && input.url) || "";
        if (CART_ROUTE.test(url)) queue();
      } catch (e) {}
      return res;
    });
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.addEventListener("load", function () {
      if (typeof url === "string" && CART_ROUTE.test(url)) queue();
    });
    return nativeOpen.apply(this, arguments);
  };

  ["cart:updated", "cart:refresh", "cart:change", "free-gift:updated"]
    .forEach(function (name) { document.addEventListener(name, queue); });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { wire(); sync(); });
  } else {
    wire(); sync();
  }

  /* The theme editor re-renders a section by replacing its DOM, which
     throws away our cached nodes and listeners. Re-bind when that happens. */
  document.addEventListener("shopify:section:load", function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector("#eco-impact-bar")) {
      el = {}; booted = false; wire(); sync();
    }
  });

  window.EcoImpactBar = {
    sync: sync,
    settings: function () {
      return { tier: TIER_CENTS, shipping: SHIP_CENTS, shippingLabel: SHIP_LABEL };
    }
  };
})();